import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import { useQuery } from '@tanstack/react-query';
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
} from 'recharts';
import axiosInstance from '../../services/api';
import { INK, FONTS, ledgerInputSx } from '../../styles/ledger';
import {
  LedgerPage,
  PageHeader,
  KPIStrip,
  KPI,
  FilterBar,
  LedgerCard,
  SectionHeader,
  HeaderIconBtn,
} from '../../components/admin/LedgerPrimitives';

interface DailyUsageRow {
  date: string;
  input_tokens: number;
  output_tokens: number;
  cost_usd: number;
  conversation_count: number;
}
interface TopUserRow {
  user_id: number;
  name: string;
  email: string;
  total_tokens: number;
  total_cost_usd: number;
  conversation_count: number;
}
interface TokenUsageSummary {
  daily_usage: DailyUsageRow[];
  top_users: TopUserRow[];
  total_cost_usd: number;
  total_tokens: number;
  conversation_count: number;
}

// Editorial muted palette — used instead of bright primary colors
const CHART_PALETTE = ['#1F4E3D', '#8E6E3D', '#5C5A52', '#A03A2A', '#2A5F8C', '#B8651B', '#3D6E5C', '#704A3A', '#7B7A6F', '#3E5773'];

const toDateString = (d: Date) => d.toISOString().slice(0, 10);

const AiTokenUsagePage: React.FC = () => {
  const [from, setFrom] = useState(toDateString(new Date(Date.now() - 7 * 86400_000)));
  const [to, setTo] = useState(toDateString(new Date()));

  const { data, isLoading, isError, refetch } = useQuery<TokenUsageSummary>({
    queryKey: ['admin', 'ai', 'token-usage', from, to],
    queryFn: async () => {
      const res = await axiosInstance.get(`/admin/ai/token-usage/summary?from=${from}&to=${to}`);
      return res.data;
    },
    staleTime: 60_000,
  });

  const tickStyle = { fontSize: 10, fill: INK.subtle, fontFamily: FONTS.mono };
  const gridStroke = INK.hairlineSoft;

  return (
    <LedgerPage eyebrowFooter="Almaship · AI agent · token usage">
      <PageHeader
        eyebrow="AI Agent · Spend"
        title={
          <>
            Token usage
            <Box component="span" sx={{ fontStyle: 'italic', color: INK.accent }}>
              .
            </Box>
          </>
        }
        description="Daily spend, top consumers, and per-user breakdown. All timestamps in UTC."
        actions={
          <HeaderIconBtn title="Refresh" onClick={() => refetch()}>
            {isLoading ? <CircularProgress size={16} sx={{ color: INK.muted }} /> : <RefreshIcon sx={{ fontSize: 18 }} />}
          </HeaderIconBtn>
        }
      />

      <FilterBar label="Range">
        <TextField
          label="From"
          type="date"
          size="small"
          value={from}
          onChange={(e) => setFrom(e.target.value)}
          InputLabelProps={{ shrink: true, sx: { fontFamily: FONTS.sans, fontSize: 13, color: INK.muted, '&.Mui-focused': { color: INK.accent } } }}
          sx={ledgerInputSx}
        />
        <TextField
          label="To"
          type="date"
          size="small"
          value={to}
          onChange={(e) => setTo(e.target.value)}
          InputLabelProps={{ shrink: true, sx: { fontFamily: FONTS.sans, fontSize: 13, color: INK.muted, '&.Mui-focused': { color: INK.accent } } }}
          sx={ledgerInputSx}
        />
      </FilterBar>

      {isError && (
        <Alert severity="error" sx={{ borderRadius: 0, border: `1px solid ${INK.inactive}`, fontFamily: FONTS.sans, mb: 2 }}>
          Failed to load token usage data.
        </Alert>
      )}

      <KPIStrip cols={3}>
        <KPI label="Total tokens" value={data?.total_tokens.toLocaleString() ?? '—'} sub="input + output" />
        <KPI label="Total cost" value={data ? `$${data.total_cost_usd.toFixed(4)}` : '—'} sub="USD this range" accent />
        <KPI label="Conversations" value={data?.conversation_count.toLocaleString() ?? '—'} sub="sessions" />
      </KPIStrip>

      <LedgerCard mb={3}>
        <SectionHeader eyebrow="Trend" title="Daily token usage" description="Input vs output across selected range" />
        {isLoading ? (
          <Box sx={{ height: 280, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <CircularProgress size={24} sx={{ color: INK.accent }} />
          </Box>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={data?.daily_usage ?? []} margin={{ top: 8, right: 16, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="2 4" stroke={gridStroke} />
              <XAxis dataKey="date" tick={tickStyle} stroke={INK.hairline} />
              <YAxis tick={tickStyle} stroke={INK.hairline} />
              <RechartsTooltip
                contentStyle={{
                  fontFamily: FONTS.sans,
                  fontSize: 12,
                  border: `1px solid ${INK.hairline}`,
                  borderRadius: 4,
                  backgroundColor: INK.surface,
                }}
              />
              <Legend wrapperStyle={{ fontFamily: FONTS.mono, fontSize: 10, letterSpacing: '0.1em', textTransform: 'uppercase' }} />
              <Line type="monotone" dataKey="input_tokens" stroke={INK.accent} strokeWidth={2} dot={false} name="Input" />
              <Line type="monotone" dataKey="output_tokens" stroke={INK.warning} strokeWidth={2} dot={false} name="Output" />
            </LineChart>
          </ResponsiveContainer>
        )}
      </LedgerCard>

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '7fr 5fr' }, gap: 2 }}>
        <LedgerCard>
          <SectionHeader eyebrow="Ranking" title="Top 10 users by tokens" />
          {isLoading ? (
            <Box sx={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={24} sx={{ color: INK.accent }} />
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <BarChart
                data={(data?.top_users ?? []).map((u) => ({ name: u.name.split(' ')[0] ?? u.email, total_tokens: u.total_tokens }))}
                margin={{ bottom: 40 }}
              >
                <CartesianGrid strokeDasharray="2 4" stroke={gridStroke} />
                <XAxis dataKey="name" tick={tickStyle} angle={-30} textAnchor="end" interval={0} stroke={INK.hairline} />
                <YAxis tick={tickStyle} stroke={INK.hairline} />
                <RechartsTooltip
                  contentStyle={{
                    fontFamily: FONTS.sans,
                    fontSize: 12,
                    border: `1px solid ${INK.hairline}`,
                    borderRadius: 4,
                    backgroundColor: INK.surface,
                  }}
                />
                <Bar dataKey="total_tokens" fill={INK.accent} name="Tokens" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </LedgerCard>

        <LedgerCard>
          <SectionHeader eyebrow="Share" title="Cost by user" />
          {isLoading ? (
            <Box sx={{ height: 260, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <CircularProgress size={24} sx={{ color: INK.accent }} />
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={260}>
              <PieChart>
                <Pie
                  data={(data?.top_users ?? []).map((u) => ({ name: u.name.split(' ')[0] ?? u.email, value: u.total_cost_usd }))}
                  cx="50%"
                  cy="50%"
                  outerRadius={80}
                  dataKey="value"
                  nameKey="name"
                  label={({ name, percent }) =>
                    percent > 0.04 ? (
                      <tspan style={{ fontFamily: FONTS.mono, fontSize: 10, fill: INK.text }}>
                        {`${name} ${(percent * 100).toFixed(0)}%`}
                      </tspan>
                    ) : null
                  }
                  labelLine={false}
                  stroke={INK.surface}
                  strokeWidth={1}
                >
                  {(data?.top_users ?? []).map((_, idx) => (
                    <Cell key={idx} fill={CHART_PALETTE[idx % CHART_PALETTE.length]} />
                  ))}
                </Pie>
                <RechartsTooltip
                  formatter={(val: number) => [`$${val.toFixed(4)}`, 'Cost']}
                  contentStyle={{
                    fontFamily: FONTS.sans,
                    fontSize: 12,
                    border: `1px solid ${INK.hairline}`,
                    borderRadius: 4,
                    backgroundColor: INK.surface,
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          )}
        </LedgerCard>
      </Box>
      <Typography sx={{ display: 'none' }}>{/* keep Typography import alive */}</Typography>
    </LedgerPage>
  );
};

export default AiTokenUsagePage;
